/**
 * Download a document through a credentialed fetch instead of a bare full-page
 * navigation (window.location.assign).
 *
 * A full-page navigation to /api/documents/:id/download replaces the running SPA
 * with the raw response — fine for a 200 attachment (the browser downloads it and
 * stays put), but for a 403 (money-blind role on a money-bearing doc), 404
 * (not found) or 410 (seeded key, no bytes — a documented demo scenario) the tab
 * navigates to the raw JSON error body and the app is gone until the user hits
 * Back. Fetching instead lets us keep the SPA mounted and surface the error via
 * `onError` (a toast), while the happy path still triggers a normal "Save as"
 * using the server's Content-Disposition filename.
 */

/**
 * iOS (iPhone/iPad, including iPadOS reporting as MacIntel with touch) — where
 * the <a download> attribute is silently ignored, so a document must be OPENED in
 * a new tab rather than downloaded (F10). Exported so the DocumentViewer can make
 * the same call for its inline PDF affordance.
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iP(hone|ad|od)/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export async function downloadDocument(
  documentId: string,
  onError?: (message: string) => void,
  // Explicit download endpoint for non-staff contexts (e.g. the client portal's
  // `/api/client-portal/documents/:id/download`). Defaults to the staff route.
  downloadUrl?: string,
): Promise<void> {
  try {
    const res = await fetch(downloadUrl || `/api/documents/${documentId}/download`, { credentials: 'same-origin' });
    if (!res.ok) {
      let message = `Download failed (${res.status}).`;
      try {
        const body = await res.json();
        if (body && typeof body === 'object' && 'error' in body) {
          message = String((body as { error: unknown }).error);
        }
      } catch { /* non-JSON error body — keep the status-code message */ }
      onError?.(message);
      return;
    }
    const blob = await res.blob();
    // Prefer the server's filename from Content-Disposition; fall back to the id.
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const filename = m ? decodeURIComponent(m[1]) : `document-${documentId}`;
    const url = URL.createObjectURL(blob);

    // F10 — iOS Safari IGNORES the <a download> attribute, so the anchor-click
    // download silently does nothing on iPhone/iPad ("PDFs can't be opened on
    // mobile"). On iOS, open the object URL in a new tab instead so the native
    // viewer renders the PDF/image. Desktop is byte-identical (this branch never
    // runs off-iOS). The revoke is deferred so the new tab can finish loading.
    if (isIOS()) {
      const win = window.open(url, '_blank');
      if (!win) onError?.('Please allow pop-ups to open this document.');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    onError?.('Download failed. Please check your connection and try again.');
  }
}
